import { alpha } from '@mui/material/styles';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { getChannelsColorPresets } from 'api/slideMultiplexColorPresets';
import { getSlidesViewerNavigationPrefetchData } from 'api/slides';
import { getColorForCellClassificationLayer } from 'components/Procedure/Infobar/slidesVisualizationAndConfiguration/helpers';
import { getChannelColorPresetSettingsByMarkerType } from 'components/Procedure/SlideControls/Multiplex/colorSettings';
import { defaultLayerColor, defaultLayerColors } from 'components/theme/theme';
import { compact, find, flatMap, isEmpty, uniq } from 'es-toolkit/compat';
import { PlotlyFigure, SectionTable } from 'interfaces/agentSession';
import { ChannelsColorPreset } from 'interfaces/channelsPreset';
import {
  JobResultDimensionMeta,
  JobResultDisplayLabelOption,
  JobResultFigureAxis,
  JobResultMergeTagsDefinition,
  JobResultSourceConfig,
} from 'interfaces/jobResultMetadata';
import { SlidePrefetchData } from 'interfaces/slide';
import { MULTIPLEX_STAIN_ID } from 'interfaces/stainType';
import React from 'react';
import useLocalStorage from 'use-local-storage';
import { ArrayParam, BooleanParam, JsonParam, StringParam } from 'use-query-params';
import escapeHtml from 'utils/escapeHtml';
import useTaxonomy, { cellTaxonomy } from 'utils/queryHooks/taxonomy/useTaxonomy';
import { useCellColorMappings } from 'utils/queryHooks/useCellColorMappings';
import { useSearchIndex } from 'utils/queryHooks/useSearchIndex';
import { useCurrentLabId } from 'utils/useCurrentLab';
import { encodeQueryParamsUsingSchema } from 'utils/useEncodedFilters/helpers';
import { useStainTypeIdToDisplayName } from 'utils/useStainTypeIdToDisplayName';
import useStudy from 'utils/useStudy';

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const JOB_RESULT_SLIDE_LABEL_MODE_STORAGE_KEY = 'jobResultSlideLabelMode';
const JOB_RESULT_SLIDE_QUERY_STALE_TIME_MS = 5 * 60 * 1000;

const jobResultSlidePrefetchSchema = {
  filters: JsonParam,
  labId: StringParam,
  slideIdsToInclude: ArrayParam,
  skipPagination: BooleanParam,
};

const isPrimitiveJobResultValue = (value: unknown): value is string | number | boolean =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';

const stringifyJobResultValue = (value: unknown) => {
  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'number' || typeof value === 'boolean') {
    return String(value);
  }

  return '';
};

const wrapHtmlWithOptionalColor = (value: string, color?: string) => {
  const escapedValue = escapeHtml(value);

  return color ? `<span style="color:${color}">${escapedValue}</span>` : escapedValue;
};

const isJobResultDimensionMeta = (value: unknown): value is JobResultDimensionMeta =>
  Boolean(value && typeof value === 'object');

const MERGE_TAG_ENTITY_TYPE_PATTERN = /\{\{\s*(slide|marker|cellType)(?:\.[^:]+)?\s*:/;
const DESCRIPTION_MERGE_TAG_PATTERN =
  /\{\{\s*(slide|marker|cellType)(?:\.(displayName|color|caseId|label|link))?\s*:\s*(?:\{([^}]+)\}|([^}]+?))\s*\}\}/g;
const DESCRIPTION_INLINE_CODE_PATTERN = /`([^`\n]+)`/g;

const getMergeTagEntityType = (mergeTags?: JobResultMergeTagsDefinition) => {
  const mergeTagValues = Object.values(mergeTags || {});

  for (const mergeTagValue of mergeTagValues) {
    if (typeof mergeTagValue !== 'string') {
      continue;
    }

    const matchedEntityType = mergeTagValue.match(MERGE_TAG_ENTITY_TYPE_PATTERN)?.[1];
    if (matchedEntityType) {
      return matchedEntityType;
    }
  }

  return undefined;
};

const getEffectiveEntityType = (meta?: JobResultDimensionMeta | null) => {
  return meta?.entityType || getMergeTagEntityType(meta?.mergeTags) || getMergeTagEntityType(meta?.itemMergeTags);
};

const getSerializedSeparator = (meta?: JobResultDimensionMeta | null) => meta?.separator || ', ';

const splitSerializedJobResultItems = (value: unknown, separator: string) => {
  if (typeof value === 'string') {
    return compact(value.split(separator).map((item) => item.trim()));
  }

  if (Array.isArray(value)) {
    return compact(value.map((item) => stringifyJobResultValue(item).trim()));
  }

  return compact([stringifyJobResultValue(value).trim()]);
};

const getSlideCaseLabel = (slideViewerLinkDetails?: SlidePrefetchData) => {
  if (slideViewerLinkDetails?.caseLabel) {
    return slideViewerLinkDetails.caseLabel;
  }

  return null;
};

const getFallbackMarkerColor = ({ markerId, markerIndex }: { markerId: string; markerIndex: number }) => {
  const normalizedMarkerId = markerId.toLowerCase();

  if (['dapi', 'hoechst', 'syto'].includes(normalizedMarkerId) || normalizedMarkerId.includes('dna')) {
    return defaultLayerColor;
  }

  return defaultLayerColors[markerIndex % defaultLayerColors.length];
};

const formatCellTypeDisplayName = ({
  cellTypeId,
  cellTypeDisplayNameById,
}: {
  cellTypeId: string;
  cellTypeDisplayNameById: Record<string, string>;
}) => {
  if (cellTypeDisplayNameById[cellTypeId]) {
    return cellTypeDisplayNameById[cellTypeId];
  }

  const [baseCellTypeId, ...cellTypeSuffixParts] = cellTypeId.split('-');
  const baseCellTypeDisplayName = cellTypeDisplayNameById[baseCellTypeId];

  if (!baseCellTypeDisplayName || !cellTypeSuffixParts.length) {
    return cellTypeId;
  }

  return `${baseCellTypeDisplayName}-${cellTypeSuffixParts.join('-')}`;
};

export const normalizeSlideLabelOptions = (options?: JobResultDisplayLabelOption[]) => {
  const normalizedOptions = compact(
    (options || []).map((option) => {
      if (option === 'displayName' || option === 'caseId') {
        return 'caseLabel';
      }

      if (option === 'label') {
        return null;
      }

      if (option === 'caseLabel' || option === 'slideId') {
        return option;
      }

      return null;
    })
  );

  const uniqueNormalizedOptions = uniq(normalizedOptions);

  return uniqueNormalizedOptions.length ? uniqueNormalizedOptions : ['caseLabel', 'slideId'];
};

const normalizeStoredSlideLabelMode = (option?: JobResultDisplayLabelOption) => {
  if (option === 'displayName' || option === 'caseId') {
    return 'caseLabel';
  }

  if (option === 'caseLabel' || option === 'slideId') {
    return option;
  }

  return undefined;
};

const normalizeFigurePath = (valueField: string) => {
  if (valueField.startsWith('data') || valueField.startsWith('layout')) {
    return valueField;
  }

  return `data[].${valueField}`;
};

const JOB_RESULT_DIMENSION_IDENTIFIER_PATTERN = /[^a-z0-9]+/gi;

const normalizeJobResultDimensionIdentifier = (value?: string | null) =>
  (value || '').replace(JOB_RESULT_DIMENSION_IDENTIFIER_PATTERN, '').toLowerCase();

type JobResultFigureAxisId = 'xaxis' | 'yaxis' | 'legend' | 'series' | 'group' | 'context';

const JOB_RESULT_FIGURE_AXIS_PATHS: Record<JobResultFigureAxisId, string[]> = {
  xaxis: ['data[].x', 'layout.xaxis.ticktext', 'layout.xaxis.categoryarray'],
  yaxis: ['data[].y', 'layout.yaxis.ticktext', 'layout.yaxis.categoryarray'],
  legend: ['data[].name', 'data[].legendgroup'],
  series: ['data[].name', 'data[].legendgroup'],
  group: ['layout.annotations[].text', 'data[].name', 'data[].legendgroup'],
  context: [],
};

const normalizeJobResultFigureAxisId = (value?: JobResultFigureAxis | null): JobResultFigureAxisId | null => {
  const normalizedCandidate = normalizeJobResultDimensionIdentifier(value);

  if (
    normalizedCandidate === 'x' ||
    normalizedCandidate === 'datax' ||
    normalizedCandidate === 'xaxis' ||
    normalizedCandidate === 'layoutxaxis' ||
    normalizedCandidate.startsWith('xaxis') ||
    normalizedCandidate.startsWith('layoutxaxis')
  ) {
    return 'xaxis';
  }

  if (
    normalizedCandidate === 'y' ||
    normalizedCandidate === 'datay' ||
    normalizedCandidate === 'yaxis' ||
    normalizedCandidate === 'layoutyaxis' ||
    normalizedCandidate.startsWith('yaxis') ||
    normalizedCandidate.startsWith('layoutyaxis')
  ) {
    return 'yaxis';
  }

  if (normalizedCandidate === 'legend' || normalizedCandidate.startsWith('legend')) {
    return 'legend';
  }

  if (normalizedCandidate === 'series' || normalizedCandidate.startsWith('series')) {
    return 'series';
  }

  if (normalizedCandidate === 'group' || normalizedCandidate.startsWith('group')) {
    return 'group';
  }

  if (normalizedCandidate === 'context' || normalizedCandidate.startsWith('context')) {
    return 'context';
  }

  return null;
};

const getJobResultDimensionFigureAxes = (dimension: JobResultDimensionMeta) => {
  return uniq(
    compact(
      [dimension.axis, dimension.figureAxis].flatMap((figureAxis) =>
        Array.isArray(figureAxis) ? compact(figureAxis) : compact([figureAxis])
      )
    )
  );
};

const isSimpleTableValueField = (valueField: string) => !valueField.includes('.') && !valueField.includes('[');

const getTableDimensionFieldCandidates = (dimension: JobResultDimensionMeta) => {
  const explicitValueFields = Array.isArray(dimension.valueField)
    ? compact(dimension.valueField)
    : compact([dimension.valueField]);

  return uniq([
    ...compact([dimension.key]),
    ...explicitValueFields.filter((valueField) => isSimpleTableValueField(valueField)),
  ]);
};

const getJobResultFigureAxisIds = ({
  key,
  valueFields,
  figureAxes,
}: {
  key?: string;
  valueFields: string[];
  figureAxes: JobResultFigureAxis[];
}) => {
  const inferredAxisIds = new Set<JobResultFigureAxisId>();

  const maybeAddAxisId = (candidate?: string | null) => {
    const axisId = normalizeJobResultFigureAxisId(candidate as JobResultFigureAxis | null);

    if (axisId) {
      inferredAxisIds.add(axisId);
    }
  };

  figureAxes.forEach(maybeAddAxisId);

  if (inferredAxisIds.size) {
    return Array.from(inferredAxisIds);
  }

  maybeAddAxisId(key);
  valueFields.forEach(maybeAddAxisId);

  return Array.from(inferredAxisIds);
};

export const getJobResultFigureValueFields = (dimension: JobResultDimensionMeta) => {
  const explicitValueFields = Array.isArray(dimension.valueField)
    ? compact(dimension.valueField)
    : compact([dimension.valueField]);
  const explicitFigureAxes = getJobResultDimensionFigureAxes(dimension);

  return uniq([
    ...explicitValueFields,
    ...getJobResultFigureAxisIds({
      key: dimension.key,
      valueFields: explicitValueFields,
      figureAxes: explicitFigureAxes,
    }).flatMap((axisId) => JOB_RESULT_FIGURE_AXIS_PATHS[axisId]),
  ]);
};

const JOB_RESULT_FIGURE_PATH_INDEX_PATTERN = /\[(\d+)\]/g;
const JOB_RESULT_FIGURE_PATH_NUMERIC_SEGMENT_PATTERN = /^\d+$/;

type ParsedPathToken =
  | {
      type: 'prop';
      key: string;
      iterate: boolean;
    }
  | {
      type: 'index';
      index: number;
    };

const parseFigurePath = (valueFieldPath: string): ParsedPathToken[] => {
  const normalizedPath = valueFieldPath.replace(JOB_RESULT_FIGURE_PATH_INDEX_PATTERN, '.$1');

  return compact(
    normalizedPath.split('.').map((segment) => {
      if (!segment) {
        return null;
      }

      if (JOB_RESULT_FIGURE_PATH_NUMERIC_SEGMENT_PATTERN.test(segment)) {
        return { type: 'index', index: Number(segment) } as ParsedPathToken;
      }

      if (segment.endsWith('[]')) {
        return {
          type: 'prop',
          key: segment.slice(0, -2),
          iterate: true,
        } as ParsedPathToken;
      }

      return {
        type: 'prop',
        key: segment,
        iterate: false,
      } as ParsedPathToken;
    })
  );
};

const collectLeafValues = (value: unknown): unknown[] => {
  if (Array.isArray(value)) {
    return flatMap(value, collectLeafValues);
  }

  return [value];
};

const collectValuesFromPath = (value: unknown, pathTokens: ParsedPathToken[]): unknown[] => {
  if (!pathTokens.length) {
    return collectLeafValues(value);
  }

  const [pathToken, ...remainingPathTokens] = pathTokens;

  if (pathToken.type === 'index') {
    return Array.isArray(value) ? collectValuesFromPath(value[pathToken.index], remainingPathTokens) : [];
  }

  if (!value || typeof value !== 'object') {
    return [];
  }

  const nestedValue = (value as Record<string, unknown>)[pathToken.key];
  if (pathToken.iterate) {
    return Array.isArray(nestedValue)
      ? flatMap(nestedValue, (item) => collectValuesFromPath(item, remainingPathTokens))
      : [];
  }

  return collectValuesFromPath(nestedValue, remainingPathTokens);
};

const mapLeafValues = (value: unknown, transformValue: (itemValue: string) => string): unknown => {
  if (Array.isArray(value)) {
    return value.map((itemValue) => mapLeafValues(itemValue, transformValue));
  }

  return isPrimitiveJobResultValue(value) ? transformValue(stringifyJobResultValue(value)) : value;
};

const transformValuesAtPath = (
  value: unknown,
  pathTokens: ParsedPathToken[],
  transformValue: (itemValue: string) => string
): unknown => {
  if (!pathTokens.length) {
    return mapLeafValues(value, transformValue);
  }

  const [pathToken, ...remainingPathTokens] = pathTokens;

  if (pathToken.type === 'index') {
    if (!Array.isArray(value) || pathToken.index >= value.length) {
      return value;
    }

    const nextValue = transformValuesAtPath(value[pathToken.index], remainingPathTokens, transformValue);
    if (nextValue === value[pathToken.index]) {
      return value;
    }

    const clonedValues = [...value];
    clonedValues[pathToken.index] = nextValue;
    return clonedValues;
  }

  if (!value || typeof value !== 'object') {
    return value;
  }

  const objectValue = value as Record<string, unknown>;
  const nestedValue = objectValue[pathToken.key];

  if (pathToken.iterate) {
    if (!Array.isArray(nestedValue)) {
      return value;
    }

    const nextValue = nestedValue.map((itemValue) =>
      transformValuesAtPath(itemValue, remainingPathTokens, transformValue)
    );
    const didChange = nextValue.some((itemValue, index) => itemValue !== nestedValue[index]);

    return didChange ? { ...objectValue, [pathToken.key]: nextValue } : value;
  }

  const nextValue = transformValuesAtPath(nestedValue, remainingPathTokens, transformValue);
  return nextValue === nestedValue ? value : { ...objectValue, [pathToken.key]: nextValue };
};

export interface JobResultResolvedValueItem {
  rawValue: string;
  displayValue: string;
  color?: string;
}

export interface JobResultResolvedValue {
  text: string;
  items: JobResultResolvedValueItem[];
  color?: string;
  htmlText?: string;
  isAugmented: boolean;
}

export interface JobResultResolverLookups {
  slideById: Record<string, SlidePrefetchData>;
  markerLabelById: Record<string, string>;
  markerColorById: Record<string, string>;
  cellTypeLabelById: Record<string, string>;
  cellTypeColorById: Record<string, string>;
}

const buildResolvedJobResultValue = ({
  items,
  separator,
  html,
}: {
  items: JobResultResolvedValueItem[];
  separator: string;
  html?: boolean;
}): JobResultResolvedValue => {
  const text = items.map((item) => item.displayValue).join(separator);
  const isAugmented = items.some((item) => item.displayValue !== item.rawValue);
  const htmlText = html
    ? items.map((item) => wrapHtmlWithOptionalColor(item.displayValue, item.color)).join(separator)
    : undefined;

  return {
    text,
    items,
    color: items.length === 1 ? items[0].color : undefined,
    htmlText,
    isAugmented,
  };
};

const getDescriptionDimensionFieldIdentifiers = (dimension: JobResultDimensionMeta) => {
  return uniq(
    compact([
      dimension.key,
      ...(Array.isArray(dimension.valueField) ? compact(dimension.valueField) : compact([dimension.valueField])),
    ]).map((value) => normalizeJobResultDimensionIdentifier(value))
  );
};

const buildDescriptionDimensionsByEntityType = (dimensions: JobResultDimensionMeta[]) => {
  return dimensions.reduce<Partial<Record<'slide' | 'marker' | 'cellType', JobResultDimensionMeta>>>(
    (currentDimensionsByEntityType, dimension) => {
      const entityType = getEffectiveEntityType(dimension);

      if (
        (entityType === 'slide' || entityType === 'marker' || entityType === 'cellType') &&
        !currentDimensionsByEntityType[entityType]
      ) {
        currentDimensionsByEntityType[entityType] = dimension;
      }

      return currentDimensionsByEntityType;
    },
    {}
  );
};

const getDescriptionEntityTypes = (dimensions: JobResultDimensionMeta[]) => {
  return uniq(
    compact(
      dimensions.map((dimension) => {
        const entityType = getEffectiveEntityType(dimension);

        return entityType === 'slide' || entityType === 'marker' || entityType === 'cellType' ? entityType : null;
      })
    )
  );
};

const shouldSkipDescriptionPlaceholder = ({
  rawValue,
  entityType,
  dimensions,
}: {
  rawValue: string;
  entityType: 'slide' | 'marker' | 'cellType';
  dimensions: JobResultDimensionMeta[];
}) => {
  const normalizedRawValue = normalizeJobResultDimensionIdentifier(rawValue);

  if (!normalizedRawValue || normalizedRawValue === 'value') {
    return true;
  }

  return dimensions.some((dimension) => {
    if (getEffectiveEntityType(dimension) !== entityType) {
      return false;
    }

    return getDescriptionDimensionFieldIdentifiers(dimension).includes(normalizedRawValue);
  });
};

const pushDescriptionEntityValue = ({
  entityType,
  rawValue,
  slideIds,
  markerIds,
  cellTypeIds,
}: {
  entityType: 'slide' | 'marker' | 'cellType';
  rawValue: string;
  slideIds: string[];
  markerIds: string[];
  cellTypeIds: string[];
}) => {
  if (entityType === 'slide') {
    slideIds.push(rawValue);
  }

  if (entityType === 'marker') {
    markerIds.push(rawValue);
  }

  if (entityType === 'cellType') {
    cellTypeIds.push(rawValue);
  }
};

const collectJobResultValuesFromDescription = ({
  description,
  dimensions,
}: {
  description?: string | null;
  dimensions: JobResultDimensionMeta[];
}) => {
  const slideIds: string[] = [];
  const markerIds: string[] = [];
  const cellTypeIds: string[] = [];

  if (!description) {
    return {
      slideIds,
      markerIds,
      cellTypeIds,
    };
  }

  const descriptionMergeTagPattern = new RegExp(DESCRIPTION_MERGE_TAG_PATTERN);
  let descriptionMatch: RegExpExecArray | null;

  while ((descriptionMatch = descriptionMergeTagPattern.exec(description))) {
    const entityType = descriptionMatch[1] as 'slide' | 'marker' | 'cellType';
    const rawValue = String(descriptionMatch[3] || descriptionMatch[4] || '').trim();

    if (!rawValue || shouldSkipDescriptionPlaceholder({ rawValue, entityType, dimensions })) {
      continue;
    }

    pushDescriptionEntityValue({
      entityType,
      rawValue,
      slideIds,
      markerIds,
      cellTypeIds,
    });
  }

  const descriptionInlineCodePattern = new RegExp(DESCRIPTION_INLINE_CODE_PATTERN);
  const descriptionEntityTypes = getDescriptionEntityTypes(dimensions);

  while ((descriptionMatch = descriptionInlineCodePattern.exec(description))) {
    const rawValue = String(descriptionMatch[1] || '').trim();

    if (!rawValue || rawValue.includes(' ')) {
      continue;
    }

    descriptionEntityTypes.forEach((entityType) => {
      pushDescriptionEntityValue({
        entityType,
        rawValue,
        slideIds,
        markerIds,
        cellTypeIds,
      });
    });
  }

  return {
    slideIds: uniq(compact(slideIds)),
    markerIds: uniq(compact(markerIds)),
    cellTypeIds: uniq(compact(cellTypeIds)),
  };
};

const resolveDescriptionJobResultValue = ({
  rawValue,
  lookups,
  slideLabelMode,
  dimensions,
  entityType,
}: {
  rawValue: string;
  lookups: JobResultResolverLookups;
  slideLabelMode: JobResultDisplayLabelOption;
  dimensions: JobResultDimensionMeta[];
  entityType?: 'slide' | 'marker' | 'cellType';
}) => {
  const dimensionsByEntityType = buildDescriptionDimensionsByEntityType(dimensions);
  const entityTypesToCheck = entityType ? [entityType] : getDescriptionEntityTypes(dimensions);

  for (const currentEntityType of entityTypesToCheck) {
    const resolvedValue = resolveJobResultDisplayValue({
      meta: dimensionsByEntityType[currentEntityType],
      rawValue,
      lookups,
      slideLabelMode,
    });

    if (entityType) {
      return resolvedValue;
    }

    if (resolvedValue.isAugmented || resolvedValue.text !== rawValue) {
      return resolvedValue;
    }
  }

  return undefined;
};

export const augmentDescriptionWithJobResultMetadata = ({
  content,
  dimensions,
  lookups,
  slideLabelMode,
}: {
  content?: string | null;
  dimensions: JobResultDimensionMeta[];
  lookups: JobResultResolverLookups;
  slideLabelMode: JobResultDisplayLabelOption;
}) => {
  if (!content || !dimensions.length) {
    return content;
  }

  const contentWithResolvedMergeTags = content.replace(
    new RegExp(DESCRIPTION_MERGE_TAG_PATTERN),
    (match, matchedEntityType, _attributeName, placeholderToken, literalToken) => {
      const entityType = matchedEntityType as 'slide' | 'marker' | 'cellType';
      const rawValue = String(placeholderToken || literalToken || '').trim();

      if (!rawValue || shouldSkipDescriptionPlaceholder({ rawValue, entityType, dimensions })) {
        return match;
      }

      const resolvedValue = resolveDescriptionJobResultValue({
        rawValue,
        lookups,
        slideLabelMode,
        dimensions,
        entityType,
      });

      return resolvedValue?.text || rawValue;
    }
  );

  return contentWithResolvedMergeTags.replace(new RegExp(DESCRIPTION_INLINE_CODE_PATTERN), (match, codeToken) => {
    const rawValue = String(codeToken || '').trim();

    if (!rawValue || rawValue.includes(' ')) {
      return match;
    }

    const resolvedValue = resolveDescriptionJobResultValue({
      rawValue,
      lookups,
      slideLabelMode,
      dimensions,
    });

    if (!resolvedValue || resolvedValue.text === rawValue) {
      return match;
    }

    return `\`${resolvedValue.text}\``;
  });
};

const resolveSingleJobResultValue = ({
  entityType,
  rawValue,
  meta,
  lookups,
  slideLabelMode,
}: {
  entityType?: string;
  rawValue: string;
  meta?: JobResultDimensionMeta | null;
  lookups: JobResultResolverLookups;
  slideLabelMode: JobResultDisplayLabelOption;
}): JobResultResolvedValueItem => {
  if (entityType === 'slide') {
    const slideViewerLinkDetails = lookups.slideById[rawValue];
    const slideLabelOptions = normalizeSlideLabelOptions(meta?.displayLabelOptions);
    const resolvedSlideLabelMode = slideLabelOptions.includes(slideLabelMode) ? slideLabelMode : slideLabelOptions[0];

    if (resolvedSlideLabelMode === 'slideId') {
      return {
        rawValue,
        displayValue: rawValue,
      };
    }

    return {
      rawValue,
      displayValue: getSlideCaseLabel(slideViewerLinkDetails) || rawValue,
    };
  }

  if (entityType === 'marker') {
    return {
      rawValue,
      displayValue: lookups.markerLabelById[rawValue] || rawValue,
      color: lookups.markerColorById[rawValue],
    };
  }

  if (entityType === 'cellType') {
    return {
      rawValue,
      displayValue: formatCellTypeDisplayName({
        cellTypeId: rawValue,
        cellTypeDisplayNameById: lookups.cellTypeLabelById,
      }),
      color: lookups.cellTypeColorById[rawValue],
    };
  }

  return {
    rawValue,
    displayValue: rawValue,
  };
};

export const resolveJobResultDisplayValue = ({
  meta,
  rawValue,
  lookups,
  slideLabelMode,
  html = false,
}: {
  meta?: JobResultDimensionMeta | null;
  rawValue: unknown;
  lookups: JobResultResolverLookups;
  slideLabelMode: JobResultDisplayLabelOption;
  html?: boolean;
}): JobResultResolvedValue => {
  const entityType = getEffectiveEntityType(meta);
  const separator = getSerializedSeparator(meta);

  if (meta?.serializedAs === 'comma_separated_string' || meta?.itemMergeTags) {
    const itemEntityType = entityType || getMergeTagEntityType(meta?.itemMergeTags);
    const resolvedItems = splitSerializedJobResultItems(rawValue, separator).map((itemValue) =>
      resolveSingleJobResultValue({
        entityType: itemEntityType,
        rawValue: itemValue,
        meta,
        lookups,
        slideLabelMode,
      })
    );

    return buildResolvedJobResultValue({
      items: resolvedItems,
      separator,
      html,
    });
  }

  const normalizedRawValue = stringifyJobResultValue(rawValue);
  const resolvedItem = resolveSingleJobResultValue({
    entityType,
    rawValue: normalizedRawValue,
    meta,
    lookups,
    slideLabelMode,
  });

  return buildResolvedJobResultValue({
    items: [resolvedItem],
    separator,
    html,
  });
};

export const buildTableColumnMetaByField = ({
  dimensions,
  table,
}: {
  dimensions?: JobResultDimensionMeta[];
  table?: SectionTable;
}) => {
  return (table?.columns || []).reduce<Record<string, JobResultDimensionMeta>>((currentColumnMetaByField, column) => {
    const matchedDimension = find(dimensions || [], (dimension) =>
      getTableDimensionFieldCandidates(dimension).some(
        (candidateField) =>
          normalizeJobResultDimensionIdentifier(candidateField) === normalizeJobResultDimensionIdentifier(column.field)
      )
    );

    if (matchedDimension || column.meta) {
      currentColumnMetaByField[column.field] = {
        ...(matchedDimension || {}),
        ...(column.meta || {}),
      };
    }

    return currentColumnMetaByField;
  }, {});
};

const collectJobResultValuesFromTable = (
  table?: SectionTable,
  columnMetaByField: Record<string, JobResultDimensionMeta> = {}
) => {
  const slideIds: string[] = [];
  const markerIds: string[] = [];
  const cellTypeIds: string[] = [];

  for (const column of table?.columns || []) {
    const columnMeta = columnMetaByField[column.field];

    if (!isJobResultDimensionMeta(columnMeta)) {
      continue;
    }

    const entityType = getEffectiveEntityType(columnMeta);
    const itemEntityType = entityType || getMergeTagEntityType(columnMeta.itemMergeTags);
    const targetEntityType =
      columnMeta.serializedAs === 'comma_separated_string' || columnMeta.itemMergeTags ? itemEntityType : entityType;

    for (const row of table?.rows || []) {
      const rawValue = (row as Record<string, unknown>)[column.field];
      const valuesToAdd =
        columnMeta.serializedAs === 'comma_separated_string' || columnMeta.itemMergeTags
          ? splitSerializedJobResultItems(rawValue, getSerializedSeparator(columnMeta))
          : compact([stringifyJobResultValue(rawValue).trim()]);

      if (targetEntityType === 'slide') {
        slideIds.push(...valuesToAdd);
      }

      if (targetEntityType === 'marker') {
        markerIds.push(...valuesToAdd);
      }

      if (targetEntityType === 'cellType') {
        cellTypeIds.push(...valuesToAdd);
      }
    }
  }

  return {
    slideIds: uniq(compact(slideIds)),
    markerIds: uniq(compact(markerIds)),
    cellTypeIds: uniq(compact(cellTypeIds)),
  };
};

const collectJobResultValuesFromFigure = ({
  figure,
  dimensions,
}: {
  figure?: PlotlyFigure | null;
  dimensions: JobResultDimensionMeta[];
}) => {
  const slideIds: string[] = [];
  const markerIds: string[] = [];
  const cellTypeIds: string[] = [];

  if (!figure) {
    return {
      slideIds,
      markerIds,
      cellTypeIds,
    };
  }

  for (const dimension of dimensions) {
    const entityType = getEffectiveEntityType(dimension);
    if (!entityType) {
      continue;
    }

    const valueFields = getJobResultFigureValueFields(dimension);

    for (const valueField of valueFields) {
      const values = compact(
        collectValuesFromPath(figure, parseFigurePath(normalizeFigurePath(valueField))).map((value) =>
          stringifyJobResultValue(value).trim()
        )
      );

      if (entityType === 'slide') {
        slideIds.push(...values);
      }

      if (entityType === 'marker') {
        markerIds.push(...values);
      }

      if (entityType === 'cellType') {
        cellTypeIds.push(...values);
      }
    }
  }

  return {
    slideIds: uniq(slideIds),
    markerIds: uniq(markerIds),
    cellTypeIds: uniq(cellTypeIds),
  };
};

const collectCachedSlidePrefetchData = ({
  queryClient,
  slideIds,
}: {
  queryClient: ReturnType<typeof useQueryClient>;
  slideIds: string[];
}) => {
  const slideIdsSet = new Set(slideIds);
  const cachedSlidesById: Record<string, SlidePrefetchData> = {};

  for (const [, cachedSlideBatch] of queryClient.getQueriesData<SlidePrefetchData[]>({
    queryKey: ['slidesViewerNavigationPrefetchData'],
  })) {
    if (!Array.isArray(cachedSlideBatch)) {
      continue;
    }

    for (const cachedSlide of cachedSlideBatch) {
      if (slideIdsSet.has(cachedSlide.slideId) && !cachedSlidesById[cachedSlide.slideId]) {
        cachedSlidesById[cachedSlide.slideId] = cachedSlide;
      }
    }
  }

  return cachedSlidesById;
};

export interface StudyReportTableAugmentation {
  columnMetaByField: Record<string, JobResultDimensionMeta>;
  resolveColumnValue: (field: string, rawValue: unknown) => JobResultResolvedValue;
}

export type JobResultTableAugmentation = StudyReportTableAugmentation;

export const getJobResultSlideLabelOptionLabel = (slideLabelOption: JobResultDisplayLabelOption) => {
  if (slideLabelOption === 'caseLabel' || slideLabelOption === 'caseId') {
    return 'Case label';
  }

  if (slideLabelOption === 'slideId') {
    return 'Slide ID';
  }

  return String(slideLabelOption);
};

const buildSlideLabelOptions = ({
  table,
  dimensions,
}: {
  table?: SectionTable;
  dimensions: JobResultDimensionMeta[];
}) => {
  const slideTableLabelOptions = flatMap(table?.columns || [], (column) => {
    const entityType = getEffectiveEntityType(column.meta);
    return entityType === 'slide' ? normalizeSlideLabelOptions(column.meta?.displayLabelOptions) : [];
  });

  const slideDimensionLabelOptions = flatMap(dimensions, (dimension) =>
    getEffectiveEntityType(dimension) === 'slide' ? normalizeSlideLabelOptions(dimension.displayLabelOptions) : []
  );

  return uniq([...slideTableLabelOptions, ...slideDimensionLabelOptions]);
};

export const augmentPlotlyFigureWithJobResultMetadata = ({
  figure,
  dimensions,
  lookups,
  slideLabelMode,
}: {
  figure: PlotlyFigure;
  dimensions: JobResultDimensionMeta[];
  lookups: JobResultResolverLookups;
  slideLabelMode: JobResultDisplayLabelOption;
}) => {
  let augmentedFigure = figure;

  for (const dimension of dimensions) {
    const valueFields = getJobResultFigureValueFields(dimension);

    for (const valueField of valueFields) {
      const normalizedFigurePath = normalizeFigurePath(valueField);
      const parsedPath = parseFigurePath(normalizedFigurePath);
      augmentedFigure = transformValuesAtPath(augmentedFigure, parsedPath, (itemValue) => {
        const resolvedValue = resolveJobResultDisplayValue({
          meta: dimension,
          rawValue: itemValue,
          lookups,
          slideLabelMode,
        });

        return resolvedValue.text || itemValue;
      }) as PlotlyFigure;
    }
  }

  return augmentedFigure;
};

export interface UseJobResultAugmentationResult {
  augmentedFigure?: PlotlyFigure | null;
  augmentedDescription?: string | null;
  tableAugmentation?: StudyReportTableAugmentation;
  showSlideLabelControl: boolean;
  slideLabelMode: JobResultDisplayLabelOption;
  slideLabelOptions: JobResultDisplayLabelOption[];
  setSlideLabelMode: (nextSlideLabelMode: JobResultDisplayLabelOption) => void;
}

interface JobResultSlideLabelModeControl {
  storedSlideLabelMode?: JobResultDisplayLabelOption;
  setStoredSlideLabelMode: (nextSlideLabelMode: JobResultDisplayLabelOption) => void;
}

const JobResultSlideLabelModeContext = React.createContext<JobResultSlideLabelModeControl | null>(null);

export const useJobResultSlideLabelModePreference = (): JobResultSlideLabelModeControl => {
  const [storedSlideLabelMode, setStoredSlideLabelMode] = useLocalStorage<JobResultDisplayLabelOption>(
    JOB_RESULT_SLIDE_LABEL_MODE_STORAGE_KEY,
    'caseLabel'
  );
  const normalizedStoredSlideLabelMode = normalizeStoredSlideLabelMode(storedSlideLabelMode);
  const handleSetStoredSlideLabelMode = React.useCallback(
    (nextSlideLabelMode: JobResultDisplayLabelOption) => {
      const normalizedNextSlideLabelMode = normalizeStoredSlideLabelMode(nextSlideLabelMode) || 'caseLabel';
      setStoredSlideLabelMode(normalizedNextSlideLabelMode);
    },
    [setStoredSlideLabelMode]
  );

  return {
    storedSlideLabelMode: normalizedStoredSlideLabelMode,
    setStoredSlideLabelMode: handleSetStoredSlideLabelMode,
  };
};

export const useJobResultSlideLabelModeControl = (): JobResultSlideLabelModeControl => {
  const contextValue = React.useContext(JobResultSlideLabelModeContext);
  const localPreference = useJobResultSlideLabelModePreference();

  return contextValue || localPreference;
};

export const JobResultSlideLabelModeProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const { storedSlideLabelMode, setStoredSlideLabelMode } = useJobResultSlideLabelModePreference();
  const contextValue = React.useMemo(
    () => ({ storedSlideLabelMode, setStoredSlideLabelMode }),
    [setStoredSlideLabelMode, storedSlideLabelMode]
  );

  return (
    <JobResultSlideLabelModeContext.Provider value={contextValue}>{children}</JobResultSlideLabelModeContext.Provider>
  );
};

export const useJobResultAugmentation = ({
  studyId,
  sourceConfig,
  table,
  figure,
  description,
}: {
  studyId?: string;
  sourceConfig?: JobResultSourceConfig | Record<string, unknown> | null;
  table?: SectionTable;
  figure?: PlotlyFigure | null;
  description?: string | null;
}): UseJobResultAugmentationResult => {
  const queryClient = useQueryClient();
  const { labId } = useCurrentLabId();
  const { stainTypeIdToDisplayName } = useStainTypeIdToDisplayName();
  const { study } = useStudy(studyId, {
    enabled: Boolean(studyId),
  });
  const dimensions = React.useMemo(
    () =>
      ((sourceConfig as JobResultSourceConfig | undefined)?.dimensions || []).filter(
        isJobResultDimensionMeta
      ) as JobResultDimensionMeta[],
    [sourceConfig]
  );
  const slideLabelOptions = React.useMemo(() => buildSlideLabelOptions({ table, dimensions }), [dimensions, table]);
  const { storedSlideLabelMode, setStoredSlideLabelMode } = useJobResultSlideLabelModeControl();
  const normalizedStoredSlideLabelMode = normalizeStoredSlideLabelMode(storedSlideLabelMode);
  const slideLabelMode =
    normalizedStoredSlideLabelMode && slideLabelOptions.includes(normalizedStoredSlideLabelMode)
      ? normalizedStoredSlideLabelMode
      : slideLabelOptions[0] || 'caseLabel';

  const columnMetaByField = React.useMemo(
    () => buildTableColumnMetaByField({ table, dimensions }),
    [dimensions, table]
  );

  const tableValues = React.useMemo(
    () => collectJobResultValuesFromTable(table, columnMetaByField),
    [columnMetaByField, table]
  );
  const figureValues = React.useMemo(
    () => collectJobResultValuesFromFigure({ figure, dimensions }),
    [dimensions, figure]
  );
  const descriptionValues = React.useMemo(
    () => collectJobResultValuesFromDescription({ description, dimensions }),
    [description, dimensions]
  );

  const slideIdsInScope = React.useMemo(
    () =>
      uniq([...tableValues.slideIds, ...figureValues.slideIds, ...descriptionValues.slideIds]).filter((id) =>
        UUID_PATTERN.test(id)
      ),
    [descriptionValues.slideIds, figureValues.slideIds, tableValues.slideIds]
  );
  const markerIdsInScope = React.useMemo(
    () => uniq([...tableValues.markerIds, ...figureValues.markerIds, ...descriptionValues.markerIds]),
    [descriptionValues.markerIds, figureValues.markerIds, tableValues.markerIds]
  );
  const cellTypeIdsInScope = React.useMemo(
    () => uniq([...tableValues.cellTypeIds, ...figureValues.cellTypeIds, ...descriptionValues.cellTypeIds]),
    [descriptionValues.cellTypeIds, figureValues.cellTypeIds, tableValues.cellTypeIds]
  );
  const { index: searchIndex } = useSearchIndex({
    enabled: Boolean(studyId) && slideIdsInScope.length > 0,
  });

  const cachedSlidesById = React.useMemo(
    () => collectCachedSlidePrefetchData({ queryClient, slideIds: slideIdsInScope }),
    [queryClient, slideIdsInScope]
  );
  const searchIndexSlidesById = React.useMemo(() => {
    const slideIdsSet = new Set(slideIdsInScope);

    return (searchIndex || []).reduce<Record<string, Partial<SlidePrefetchData> & { slideId: string }>>(
      (currentSlidesById, searchIndexEntry) => {
        if (studyId && searchIndexEntry.studyId !== studyId) {
          return currentSlidesById;
        }

        for (const slide of searchIndexEntry.slides || []) {
          if (!slideIdsSet.has(slide.id)) {
            continue;
          }

          currentSlidesById[slide.id] = {
            ...currentSlidesById[slide.id],
            slideId: slide.id,
            caseId: typeof searchIndexEntry.caseId === 'number' ? searchIndexEntry.caseId : undefined,
            caseLabel: searchIndexEntry.caseLabel,
          };
        }

        return currentSlidesById;
      },
      {}
    );
  }, [searchIndex, slideIdsInScope, studyId]);
  const missingSlideIds = React.useMemo(
    () => slideIdsInScope.filter((slideId) => !cachedSlidesById[slideId]),
    [cachedSlidesById, slideIdsInScope]
  );

  const jobResultSlidesQueryParams = React.useMemo(() => {
    if (!studyId || !labId || !missingSlideIds.length) {
      return null;
    }

    return encodeQueryParamsUsingSchema(
      {
        filters: { studyId },
        labId,
        slideIdsToInclude: missingSlideIds,
        skipPagination: true,
      },
      jobResultSlidePrefetchSchema,
      { arrayFormat: 'repeat' }
    );
  }, [labId, missingSlideIds, studyId]);

  const { data: queriedSlides = [] } = useQuery({
    queryKey: ['jobResultSlidesViewerNavigationPrefetchData', jobResultSlidesQueryParams],
    queryFn: ({ signal }) => getSlidesViewerNavigationPrefetchData(jobResultSlidesQueryParams || '', signal),
    enabled: Boolean(jobResultSlidesQueryParams),
    staleTime: JOB_RESULT_SLIDE_QUERY_STALE_TIME_MS,
  });

  const { data: channelPresets } = useQuery<ChannelsColorPreset[]>({
    queryKey: ['channelPresets', labId],
    queryFn: () => getChannelsColorPresets(labId),
    enabled: Boolean(labId) && Boolean(studyId) && markerIdsInScope.length > 0,
  });

  const { data: taxonomies, idToDisplayNameMap } = useTaxonomy(cellTaxonomy);
  const { data: cellColorMappings } = useCellColorMappings({
    enabled: cellTypeIdsInScope.length > 0,
  });

  const slidesById = React.useMemo(
    () =>
      [...Object.values(cachedSlidesById), ...queriedSlides].reduce<Record<string, SlidePrefetchData>>(
        (currentSlidesById, slide) => {
          const searchIndexSlide = searchIndexSlidesById[slide.slideId];

          currentSlidesById[slide.slideId] = {
            ...slide,
            ...(searchIndexSlide || {}),
            slideId: slide.slideId,
          };
          return currentSlidesById;
        },
        Object.entries(searchIndexSlidesById).reduce<Record<string, SlidePrefetchData>>(
          (currentSlidesById, [slideId, slide]) => {
            currentSlidesById[slideId] = {
              slideId,
              id: slideId,
              slideFormat: '',
              ...(slide as Partial<SlidePrefetchData>),
            } as SlidePrefetchData;
            return currentSlidesById;
          },
          {}
        )
      ),
    [cachedSlidesById, queriedSlides, searchIndexSlidesById]
  );

  const studyDefaultColorPreset = React.useMemo(
    () => find(channelPresets, { id: study?.settings?.defaultMultiplexColorPreset }),
    [channelPresets, study?.settings?.defaultMultiplexColorPreset]
  );

  const markerLabelById = React.useMemo(
    () =>
      markerIdsInScope.reduce<Record<string, string>>((currentMarkerLabelById, markerId) => {
        currentMarkerLabelById[markerId] = stainTypeIdToDisplayName(markerId) || markerId;
        return currentMarkerLabelById;
      }, {}),
    [markerIdsInScope, stainTypeIdToDisplayName]
  );

  const markerColorById = React.useMemo(
    () =>
      markerIdsInScope.reduce<Record<string, string>>((currentMarkerColorById, markerId, markerIndex) => {
        const presetColor = getChannelColorPresetSettingsByMarkerType(
          markerId,
          studyDefaultColorPreset?.options || {}
        )?.color;
        currentMarkerColorById[markerId] = presetColor || getFallbackMarkerColor({ markerId, markerIndex });
        return currentMarkerColorById;
      }, {}),
    [markerIdsInScope, studyDefaultColorPreset]
  );

  const resolvedCellTypeStainTypeId =
    ((sourceConfig as JobResultSourceConfig | undefined)?.stainTypeId as string | undefined) || MULTIPLEX_STAIN_ID;

  const cellTypeColorById = React.useMemo(
    () =>
      cellTypeIdsInScope.reduce<Record<string, string>>((currentCellTypeColorById, cellTypeId) => {
        const resolvedColor = getColorForCellClassificationLayer({
          layerName: cellTypeId,
          taxonomies,
          cellColorMappings,
          stainTypeId: resolvedCellTypeStainTypeId,
        });

        if (resolvedColor) {
          currentCellTypeColorById[cellTypeId] = resolvedColor;
        }

        return currentCellTypeColorById;
      }, {}),
    [cellColorMappings, cellTypeIdsInScope, resolvedCellTypeStainTypeId, taxonomies]
  );

  const lookups = React.useMemo<JobResultResolverLookups>(
    () => ({
      slideById: slidesById,
      markerLabelById,
      markerColorById,
      cellTypeLabelById: idToDisplayNameMap,
      cellTypeColorById,
    }),
    [cellTypeColorById, idToDisplayNameMap, markerColorById, markerLabelById, slidesById]
  );

  const resolveColumnValue = React.useCallback(
    (field: string, rawValue: unknown) =>
      resolveJobResultDisplayValue({
        meta: columnMetaByField[field],
        rawValue,
        lookups,
        slideLabelMode,
      }),
    [columnMetaByField, lookups, slideLabelMode]
  );

  const tableAugmentation = React.useMemo<StudyReportTableAugmentation | undefined>(
    () =>
      table
        ? {
            columnMetaByField,
            resolveColumnValue,
          }
        : undefined,
    [columnMetaByField, resolveColumnValue, table]
  );

  const augmentedFigure = React.useMemo(
    () =>
      figure && !isEmpty(dimensions)
        ? augmentPlotlyFigureWithJobResultMetadata({
            figure,
            dimensions,
            lookups,
            slideLabelMode,
          })
        : figure,
    [dimensions, figure, lookups, slideLabelMode]
  );

  const augmentedDescription = React.useMemo(
    () =>
      augmentDescriptionWithJobResultMetadata({
        content: description,
        dimensions,
        lookups,
        slideLabelMode,
      }),
    [description, dimensions, lookups, slideLabelMode]
  );

  return {
    augmentedFigure,
    augmentedDescription,
    tableAugmentation,
    showSlideLabelControl: slideLabelOptions.length > 1,
    slideLabelMode,
    slideLabelOptions,
    setSlideLabelMode: setStoredSlideLabelMode,
  };
};

export const getJobResultValueChipSx = (color?: string) => {
  if (!color) {
    return undefined;
  }

  return {
    bgcolor: alpha(color, 0.12),
    borderColor: alpha(color, 0.32),
    color,
    '& .MuiChip-label': {
      fontWeight: 600,
    },
  };
};
